import { displayCode } from "@/constants/coverCodes";
import { colorToHex, findColor } from "@/constants/colors";
import { effectiveColor } from "@/components/CustomColorNote";

const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

// "Red · 红色" — canonical English name plus the Chinese translation.
// Unknown / custom colours print as typed.
function colorLabel(name) {
  const c = findColor(name);
  if (!c) return name || "—";
  return c.zh ? `${c.name} · ${c.zh}` : c.name;
}

// Opens a print window with a copy of the production order lines:
// PO · Vehicle · Color · Codes · Clamps. Header: "For Production / 生产用" + date + user.
export function printProductionSheet({ orders, user, lang, t }) {
  const zh = lang === "zh";
  const rows = orders.map((o) => {
    const vehicle = [o.brand, o.model, o.year].filter(Boolean).join(" · ") || "—";
    const codes = o.front_cover_code
      ? `${displayCode(o.front_cover_code, "front", lang)} / ${displayCode(o.rear_cover_code, "rear", lang)}`
      : "—";
    const clamps = [o.f_clamp_code, o.r_clamp_code].some(Boolean)
      ? `${o.f_clamp_code || "—"} / ${o.r_clamp_code || "—"}`
      : "—";
    const printColor = effectiveColor(o).color;
    const hex = colorToHex(printColor);
    // Inline SVG dot — prints in colour even when "background graphics" is
    // off in the browser print dialog. Light colours get a darker outline.
    const lum = hex ? (() => { const n = parseInt(hex.slice(1), 16); return (0.299 * (n >> 16) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255; })() : 1;
    const dot = hex
      ? `<svg class="sw" viewBox="0 0 20 20" aria-hidden="true"><circle cx="10" cy="10" r="9" fill="${esc(hex)}" stroke="${lum > 0.85 ? "#475569" : "#0f172a"}" stroke-width="1"/></svg>`
      : `<span class="sw sw-empty"></span>`;
    const nameStyle = hex && lum < 0.7 ? ` style="color:${esc(hex)}"` : "";
    return `<tr>
      <td class="po">${esc(o.po_number)}</td>
      <td>${esc(vehicle)}</td>
      <td class="color">${dot}<span class="cname"${nameStyle}>${esc(colorLabel(printColor))}</span></td>
      <td class="mono">${esc(codes)}</td>
      <td class="mono">${esc(clamps)}</td>
      <td class="sig"></td>
    </tr>`;
  }).join("");
  const printedOn = new Date().toLocaleString(zh ? "zh-CN" : "en-CA");
  const who = user?.name || user?.email || "";
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>For Production · 生产用</title>
  <style>
    body{font-family:-apple-system,Segoe UI,Roboto,"Noto Sans SC","PingFang SC",sans-serif;color:#0f172a;margin:24px}
    h1{font-size:22px;margin:0 0 4px;letter-spacing:.02em}
    h1 small{font-size:16px;font-weight:600;color:#475569;margin-left:10px}
    .meta{font-size:12px;color:#475569;margin-bottom:14px}
    table{width:100%;border-collapse:collapse;font-size:13px}
    th,td{border:1px solid #cbd5e1;padding:7px 9px;text-align:left;vertical-align:middle}
    th{background:#f1f5f9;font-size:11px;text-transform:uppercase;letter-spacing:.06em;color:#475569}
    td.po{font-weight:700;font-family:ui-monospace,Menlo,Consolas,monospace}
    td.mono{font-family:ui-monospace,Menlo,Consolas,monospace}
    td.sig{width:120px}
    td.color{white-space:nowrap}
    .sw{display:inline-block;width:17px;height:17px;border-radius:50%;margin-right:8px;vertical-align:-3px;-webkit-print-color-adjust:exact;print-color-adjust:exact}
    .sw-empty{border:1px solid #94a3b8;background:#fff}
    .cname{font-weight:700;-webkit-print-color-adjust:exact;print-color-adjust:exact}
    @media print{body{margin:10mm} *{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
  </style></head><body>
  <h1>FOR PRODUCTION <small>生产用</small></h1>
  <div class="meta">${esc(t("Printed on"))}: ${esc(printedOn)} · ${esc(t("Printed by"))}: ${esc(who)} · ${orders.length} ${esc(t("order(s)"))}</div>
  <table><thead><tr>
    <th>${esc(t("PO #"))}</th><th>${esc(t("Vehicle"))}</th><th>${esc(t("Color"))}</th><th>${esc(t("Codes"))}</th><th>${esc(t("Clamps"))}</th><th>${esc(t("Signature"))}</th>
  </tr></thead><tbody>${rows}</tbody></table>
  <script>window.addEventListener('load',function(){setTimeout(function(){window.focus();window.print();},150);});</script>
  </body></html>`;
  const w = window.open("", "_blank", "width=1000,height=800");
  if (!w) return false;
  w.document.open();
  w.document.write(html);
  w.document.close();
  return true;
}
